import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import LocationCityOutlined from '@mui/icons-material/LocationCityOutlined';
import API_BASE from '../config/api';

// ─── Enums ──────────────────────────────────────────────────────────────────
const VENUE_TYPE_ENUM = ['Arena', 'Theater', 'Club', 'Festival Grounds', 'Convention Center', 'Hotel', 'Outdoor', 'Other'];
const STAGE_TYPE_ENUM = ['Permanent', 'Mobile', 'Thrust', 'In-the-Round', 'Other'];
const DOCK_TYPE_ENUM = ['Drive-In', 'Dock High', 'Ramp', 'Grade Level', 'Other'];
const PLATE_TYPE_ENUM = ['Venue Plate', 'Portable', 'None'];
const AUDIO_CONFIGURATION_ENUM = ['Mono', 'Stereo', 'L/C/R', 'L/R + Sub', 'Line Array', 'Point Source', 'Distributed', 'LRSF', 'Other'];
const LIGHTING_CONTROL_ENUM = ['DMX', 'ArtNet', 'sACN', 'MIDI', 'OSC', 'Other'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const COUNTRIES = ['United States','Canada','United Kingdom','Australia','Germany','France','Spain','Italy','Japan','Brazil','Mexico','Other'];

const TABS = ['Details', 'Contacts', 'Rooms', 'Loading & Equipment', 'Power & Rigging', 'Production'];

// ─── Styles ──────────────────────────────────────────────────────────────────
const SELECT_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")";

const INPUT_STYLE = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  padding: '4px 0',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

// ─── Path helpers ─────────────────────────────────────────────────────────────
function getPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, k) => acc?.[k], obj);
}

function setPath(obj, path, value) {
  const keys = path.split('.');
  if (keys.length === 1) return { ...obj, [keys[0]]: value };
  return { ...obj, [keys[0]]: setPath(obj[keys[0]] || {}, keys.slice(1).join('.'), value) };
}

// ─── Init form data ───────────────────────────────────────────────────────────
function initFormData(v) {
  return {
    name: v.name || '',
    venue_type: v.venue_type || '',
    website: v.website || '',
    is_hotel: !!v.is_hotel,
    address1: v.address1 || '',
    address2: v.address2 || '',
    city: v.city || '',
    state: v.state || '',
    zip: v.zip || '',
    country: v.country || '',
    capacity: {
      total: v.capacity?.total ?? '',
      floor: v.capacity?.floor ?? '',
      orchestra: v.capacity?.orchestra ?? '',
      level_100: v.capacity?.level_100 ?? '',
      level_200: v.capacity?.level_200 ?? '',
      level_300: v.capacity?.level_300 ?? '',
      balcony: v.capacity?.balcony ?? '',
      lawn: v.capacity?.lawn ?? '',
      vip: v.capacity?.vip ?? '',
    },
    network: {
      available: !!v.network?.available,
      wifi_name: v.network?.wifi_name || '',
      wifi_password: v.network?.wifi_password || '',
      hardline_location: v.network?.hardline_location || '',
    },
    stage: {
      type: v.stage?.type || '',
      height: v.stage?.height ?? '',
      house_drape: !!v.stage?.house_drape,
      house_drape_notes: v.stage?.house_drape_notes || '',
      barricade: !!v.stage?.barricade,
      barricade_notes: v.stage?.barricade_notes || '',
      mobile_stage_notes: v.stage?.mobile_stage_notes || '',
      attribute_notes: v.stage?.attribute_notes || '',
      provider: v.stage?.provider || '',
    },
    rigging: {
      available: !!v.rigging?.available,
      trim_height: v.rigging?.trim_height ?? '',
      grid_weight: v.rigging?.grid_weight ?? '',
      high_steel: v.rigging?.high_steel ?? '',
      low_steel: v.rigging?.low_steel ?? '',
      grid_access: v.rigging?.grid_access || '',
      scoreboard: !!v.rigging?.scoreboard,
      scoreboard_trim: v.rigging?.scoreboard_trim ?? '',
      scoreboard_moves: !!v.rigging?.scoreboard_moves,
      diagram_available: !!v.rigging?.diagram_available,
      diagram_file: v.rigging?.diagram_file || '',
      notes: v.rigging?.notes || '',
    },
    loading: {
      has_dock: !!v.loading?.has_dock,
      load_in_area_desc: v.loading?.load_in_area_desc || '',
      access_notes: v.loading?.access_notes || '',
      dock_address1: v.loading?.dock_address1 || '',
      dock_address2: v.loading?.dock_address2 || '',
      dock_city: v.loading?.dock_city || '',
      dock_state: v.loading?.dock_state || '',
      dock_zip: v.loading?.dock_zip || '',
      dock_country: v.loading?.dock_country || '',
      docks: (v.loading?.docks || []).map((d) => ({
        dock_number: d.dock_number || '',
        dock_type: d.dock_type || '',
        distance_to_stage: d.distance_to_stage ?? '',
        plate_available: !!d.plate_available,
        plate_type: d.plate_type || '',
        door_width: d.door_width ?? '',
        assignment: d.assignment || '',
        notes: d.notes || '',
      })),
    },
    power: (v.power || []).map((p) => ({
      type: p.type || '',
      amperage: p.amperage || '',
      phase: p.phase || '',
      voltage: p.voltage || '',
      transformer_needed: !!p.transformer_needed,
      location: p.location || '',
      distance_to_usc: p.distance_to_usc ?? '',
    })),
    production: {
      audio: {
        description: v.production?.audio?.description || '',
        patch: v.production?.audio?.patch || '',
        configuration: v.production?.audio?.configuration || '',
      },
      lighting: {
        description: v.production?.lighting?.description || '',
        control_language: v.production?.lighting?.control_language || '',
        console_file: v.production?.lighting?.console_file || '',
        plot_file: v.production?.lighting?.plot_file || '',
        patch_file: v.production?.lighting?.patch_file || '',
      },
      video: {
        description: v.production?.video?.description || '',
        patch: v.production?.video?.patch || '',
        raster_file: v.production?.video?.raster_file || '',
        format: v.production?.video?.format || '',
        input: v.production?.video?.input || '',
      },
    },
    rooms: (v.rooms || []).map((r) => ({
      title: r.title || '',
      location: r.location || '',
      room_type: r.room_type || '',
      notes: r.notes || '',
    })),
    equipment: (v.equipment || []).map((e) => ({
      equipment_type: e.equipment_type || '',
      load_capacity: e.load_capacity ?? '',
      height: e.height ?? '',
      length: e.length ?? '',
      width: e.width ?? '',
      footprint: e.footprint || '',
    })),
    labor: {
      types_available: (v.labor?.types_available || []).join(', '),
      notes: v.labor?.notes || '',
    },
    contacts: (v.contacts || []).map((c) => ({
      title: c.title || '',
      contact_types: (c.contact_types || []).join(', '),
      is_primary: !!c.is_primary,
      personnel_profile_id: c.personnel_profile_id || '',
    })),
    cad: {
      available: !!v.cad?.available,
      file_type: v.cad?.file_type || '',
      url: v.cad?.url || '',
    },
    camera_risers: {
      available: !!v.camera_risers?.available,
      notes: v.camera_risers?.notes || '',
    },
    documents: v.documents || [],
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function InlineField({ value, onChange, onBlur, onFocus, placeholder, focused }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={placeholder}
      style={{
        ...INPUT_STYLE,
        borderBottom: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
      }}
    />
  );
}

function InlineSelect({ value, onChange, onBlur, onFocus, options, placeholder = '—', focused }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={onFocus}
      style={{
        ...INPUT_STYLE,
        borderBottom: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        color: value ? '#F5F5F5' : '#888888',
        backgroundImage: SELECT_ARROW,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 4px center',
        paddingRight: '20px',
      }}
    >
      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#888888' }}>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ backgroundColor: '#1a1a1a', color: '#F5F5F5' }}>{opt}</option>
      ))}
    </select>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 500,
      fontSize: '11px',
      color: '#565656',
      marginBottom: '4px',
      letterSpacing: '0.5px',
    }}>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 500,
      fontSize: '11px',
      color: '#565656',
      letterSpacing: '2px',
      marginBottom: '12px',
    }}>
      {children}
    </div>
  );
}

function Indicator({ saved, error }) {
  if (error) return <span style={{ fontSize: '11px', color: '#BF2634', fontStyle: 'italic' }}>Error</span>;
  if (saved) return <span style={{ fontSize: '11px', color: '#39FF14' }}>Saved</span>;
  return null;
}

function AddItemBtn({ onClick, label }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'transparent',
        border: `1px dashed ${h ? '#39FF14' : '#565656'}`,
        color: h ? '#39FF14' : '#888888',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        marginTop: '8px',
      }}
    >
      {label}
    </button>
  );
}

function GreenAddBtn({ onClick, label }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: h ? 'rgba(57,255,20,0.08)' : 'transparent',
        border: '1px solid #39FF14',
        color: '#39FF14',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        marginTop: '8px',
      }}
    >
      {label}
    </button>
  );
}

function RemoveItemBtn({ onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'none',
        border: 'none',
        color: h ? '#BF2634' : '#565656',
        cursor: 'pointer',
        padding: '2px 4px',
        lineHeight: 1,
        transition: 'color 0.15s ease',
        fontSize: '15px',
        fontFamily: 'monospace',
      }}
    >
      ✕
    </button>
  );
}

function NoSelection() {
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#121212',
    }}>
      <div style={{
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: '15px',
        color: '#565656',
      }}>
        Select a venue to view details
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VenueDetail({ venueId, onVenueUpdate }) {
  const { token } = useAuth();
  const [venue, setVenue] = useState(null);
  const [formData, setFormData] = useState({});
  const formDataRef = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState('Details');
  const [savedFields, setSavedFields] = useState({});
  const [errorFields, setErrorFields] = useState({});
  const [focused, setFocused] = useState({});

  useEffect(() => { formDataRef.current = formData; }, [formData]);

  useEffect(() => {
    if (!venueId) return;
    setIsLoading(true);
    setFetchError(null);
    setActiveTab('Details');
    axios
      .get(`${API_BASE}/api/venues/${venueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setVenue(res.data);
        setFormData(initFormData(res.data));
      })
      .catch(() => setFetchError('Failed to load venue.'))
      .finally(() => setIsLoading(false));
  }, [venueId, token]);

  if (!venueId) return <NoSelection />;

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#121212' }}>
        <span style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '14px' }}>Loading...</span>
      </div>
    );
  }
  if (fetchError) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#121212' }}>
        <span style={{ color: '#BF2634', fontFamily: "'Roboto', sans-serif", fontSize: '14px' }}>{fetchError}</span>
      </div>
    );
  }
  if (!venue) return null;

  // ─── Core save helpers ──────────────────────────────────────────────────────
  async function saveImmediately(path, value) {
    try {
      const res = await axios.put(
        `${API_BASE}/api/venues/${venue._id}`,
        { [path]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVenue(res.data);
      if (onVenueUpdate) onVenueUpdate(res.data);
      setSavedFields((p) => ({ ...p, [path]: true }));
      setTimeout(() => setSavedFields((p) => ({ ...p, [path]: false })), 2000);
    } catch {
      setErrorFields((p) => ({ ...p, [path]: true }));
      setTimeout(() => setErrorFields((p) => ({ ...p, [path]: false })), 3000);
    }
  }

  function handleChange(path, value) {
    setFormData((prev) => setPath(prev, path, value));
  }

  async function handleBlur(path) {
    await saveImmediately(path, getPath(formDataRef.current, path));
  }

  async function handleSelectChange(path, value) {
    handleChange(path, value);
    await saveImmediately(path, value);
  }

  async function handleCheckboxChange(path, value) {
    handleChange(path, value);
    await saveImmediately(path, value);
  }

  function handleArrayItemChange(arrayPath, idx, field, value) {
    setFormData((prev) => {
      const arr = [...(getPath(prev, arrayPath) || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return setPath(prev, arrayPath, arr);
    });
  }

  async function handleArrayBlur(arrayPath) {
    await saveImmediately(arrayPath, getPath(formDataRef.current, arrayPath));
  }

  async function handleArrayItemSelectChange(arrayPath, idx, field, value) {
    const arr = [...(getPath(formDataRef.current, arrayPath) || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    setFormData((prev) => setPath(prev, arrayPath, arr));
    await saveImmediately(arrayPath, arr);
  }

  async function handleArrayItemCheckbox(arrayPath, idx, field, value) {
    const arr = [...(getPath(formDataRef.current, arrayPath) || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    setFormData((prev) => setPath(prev, arrayPath, arr));
    await saveImmediately(arrayPath, arr);
  }

  async function handleAddArrayItem(arrayPath, defaultItem) {
    const arr = [...(getPath(formDataRef.current, arrayPath) || []), defaultItem];
    setFormData((prev) => setPath(prev, arrayPath, arr));
    await saveImmediately(arrayPath, arr);
  }

  async function handleRemoveArrayItem(arrayPath, idx) {
    const arr = (getPath(formDataRef.current, arrayPath) || []).filter((_, i) => i !== idx);
    setFormData((prev) => setPath(prev, arrayPath, arr));
    await saveImmediately(arrayPath, arr);
  }

  // ─── Field render helpers ────────────────────────────────────────────────────
  const F = (path, label, placeholder, fullWidth) => {
    const fk = path;
    return (
      <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
        {label && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <FieldLabel>{label}</FieldLabel>
            <Indicator saved={savedFields[fk]} error={errorFields[fk]} />
          </div>
        )}
        <InlineField
          value={getPath(formData, path) ?? ''}
          onChange={(v) => handleChange(path, v)}
          onBlur={() => { setFocused((p) => ({ ...p, [fk]: false })); handleBlur(path); }}
          onFocus={() => setFocused((p) => ({ ...p, [fk]: true }))}
          focused={focused[fk]}
          placeholder={placeholder}
        />
      </div>
    );
  };

  const S = (path, label, options, placeholder, fullWidth) => {
    const fk = path;
    return (
      <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
        {label && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <FieldLabel>{label}</FieldLabel>
            <Indicator saved={savedFields[fk]} error={errorFields[fk]} />
          </div>
        )}
        <InlineSelect
          value={getPath(formData, path) || ''}
          onChange={(v) => handleSelectChange(path, v)}
          options={options}
          placeholder={placeholder || '—'}
          focused={focused[fk]}
          onFocus={() => setFocused((p) => ({ ...p, [fk]: true }))}
          onBlur={() => setFocused((p) => ({ ...p, [fk]: false }))}
        />
      </div>
    );
  };

  // Array item field helper
  const AF = (arrayPath, idx, field, label, placeholder, fullWidth) => {
    const fk = `${arrayPath}.${idx}.${field}`;
    const arr = getPath(formData, arrayPath) || [];
    return (
      <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
        {label && <FieldLabel>{label}</FieldLabel>}
        <InlineField
          value={arr[idx]?.[field] ?? ''}
          onChange={(v) => handleArrayItemChange(arrayPath, idx, field, v)}
          onBlur={() => { setFocused((p) => ({ ...p, [fk]: false })); handleArrayBlur(arrayPath); }}
          onFocus={() => setFocused((p) => ({ ...p, [fk]: true }))}
          focused={focused[fk]}
          placeholder={placeholder}
        />
      </div>
    );
  };

  // Array item select helper
  const AS = (arrayPath, idx, field, label, options, placeholder, fullWidth) => {
    const arr = getPath(formData, arrayPath) || [];
    return (
      <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
        {label && <FieldLabel>{label}</FieldLabel>}
        <InlineSelect
          value={arr[idx]?.[field] || ''}
          onChange={(v) => handleArrayItemSelectChange(arrayPath, idx, field, v)}
          options={options}
          placeholder={placeholder || '—'}
        />
      </div>
    );
  };

  const GRID2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' };
  const GRID3 = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px 24px' };
  const SEC = { marginBottom: '28px' };
  const ITEM_CARD = {
    backgroundColor: '#1e1e1e',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '8px',
  };
  const CB_LABEL = {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '14px', color: '#F5F5F5', cursor: 'pointer',
  };
  const CB_INPUT = { accentColor: '#39FF14', width: '14px', height: '14px', cursor: 'pointer' };

  // ─── Tab renderers ───────────────────────────────────────────────────────────
  function renderDetails() {
    return (
      <div>
        {/* IDENTITY */}
        <div style={SEC}>
          <SectionLabel>IDENTITY</SectionLabel>
          <div style={GRID2}>
            {F('name', 'Name', 'Venue name')}
            {S('venue_type', 'Type', VENUE_TYPE_ENUM, '— Type —')}
            {F('website', 'Website', 'https://')}
            <div>
              <FieldLabel>Hotel</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.is_hotel}
                  onChange={(e) => handleCheckboxChange('is_hotel', e.target.checked)}
                  style={CB_INPUT}
                />
                This is a hotel
              </label>
            </div>
          </div>
        </div>

        {/* ADDRESS */}
        <div style={SEC}>
          <SectionLabel>ADDRESS</SectionLabel>
          <div style={GRID2}>
            {F('address1', 'Address', 'Address line 1', true)}
            {F('address2', '', 'Address line 2', true)}
            {F('city', 'City', 'City')}
            {S('state', 'State', US_STATES, '— State —')}
            {F('zip', 'Zip', 'Zip code')}
            {S('country', 'Country', COUNTRIES, '— Country —')}
          </div>
        </div>

        {/* CAPACITY */}
        <div style={SEC}>
          <SectionLabel>CAPACITY</SectionLabel>
          <div style={GRID3}>
            {F('capacity.total', 'Total', '')}
            {F('capacity.floor', 'Floor', '')}
            {F('capacity.orchestra', 'Orchestra', '')}
            {F('capacity.level_100', 'Level 100', '')}
            {F('capacity.level_200', 'Level 200', '')}
            {F('capacity.level_300', 'Level 300', '')}
            {F('capacity.balcony', 'Balcony', '')}
            {F('capacity.lawn', 'Lawn', '')}
            {F('capacity.vip', 'VIP', '')}
          </div>
        </div>

        {/* NETWORK */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>NETWORK</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.network?.available}
                onChange={(e) => handleCheckboxChange('network.available', e.target.checked)}
                style={CB_INPUT}
              />
              Available
            </label>
          </div>
          <div style={GRID2}>
            {F('network.wifi_name', 'Wi-Fi Name', 'Network name')}
            {F('network.wifi_password', 'Wi-Fi Password', 'Password')}
            {F('network.hardline_location', 'Hardline Location', 'Location', true)}
          </div>
        </div>

        {/* CAD */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>CAD</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.cad?.available}
                onChange={(e) => handleCheckboxChange('cad.available', e.target.checked)}
                style={CB_INPUT}
              />
              Available
            </label>
          </div>
          <div style={GRID2}>
            {F('cad.file_type', 'File Type', 'e.g. DWG, PDF')}
            {F('cad.url', 'URL', 'https://', true)}
          </div>
        </div>

        {/* LABOR */}
        <div style={SEC}>
          <SectionLabel>LABOR</SectionLabel>
          <div style={{ marginBottom: '20px' }}>
            {F('labor.notes', 'Labor Notes', '', true)}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <FieldLabel>LABOR TYPES AVAILABLE</FieldLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {(formData.labor?.types_available || '').split(',').map((s) => s.trim()).filter(Boolean).length === 0 ? (
                <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656' }}>None added</span>
              ) : (
                (formData.labor?.types_available || '').split(',').map((s) => s.trim()).filter(Boolean).map((tag) => (
                  <span
                    key={tag}
                    style={{
                      backgroundColor: 'rgba(57,255,20,0.08)',
                      border: '1px solid rgba(57,255,20,0.2)',
                      borderRadius: '10px',
                      padding: '3px 10px',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '12px',
                      color: '#39FF14',
                    }}
                  >
                    {tag}
                  </span>
                ))
              )}
            </div>
          </div>
          <div style={GRID2}>
            <div>
              <FieldLabel>House Vendor</FieldLabel>
              <div style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '13px',
                color: '#565656',
                fontStyle: 'italic',
                borderBottom: '1px solid #2a2a2a',
                padding: '4px 0',
              }}>
                Vendor linking coming soon
              </div>
            </div>
            <div>
              <FieldLabel>Stage Provider</FieldLabel>
              <div style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '13px',
                color: '#565656',
                fontStyle: 'italic',
                borderBottom: '1px solid #2a2a2a',
                padding: '4px 0',
              }}>
                Vendor linking coming soon
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderContacts() {
    const contacts = formData.contacts || [];
    return (
      <div>
        <SectionLabel>CONTACTS</SectionLabel>
        {contacts.length === 0 && (
          <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '12px' }}>
            No contacts added yet.
          </div>
        )}
        {contacts.map((c, i) => (
          <div key={i} style={ITEM_CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: '#888888' }}>
                Contact {i + 1}
              </div>
              <RemoveItemBtn onClick={() => handleRemoveArrayItem('contacts', i)} />
            </div>
            <div style={GRID2}>
              {AF('contacts', i, 'title', 'Title', 'e.g. Production Manager')}
              {AF('contacts', i, 'contact_types', 'Contact Types', 'e.g. Audio, Lighting')}
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!c.is_primary}
                  onChange={(e) => handleArrayItemCheckbox('contacts', i, 'is_primary', e.target.checked)}
                  style={CB_INPUT}
                />
                Primary Contact
              </label>
            </div>
            <div style={{ marginTop: '12px' }}>
              <FieldLabel>Personnel</FieldLabel>
              <div style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: '13px',
                color: '#565656',
                fontStyle: 'italic',
                borderBottom: '1px solid #2a2a2a',
                padding: '4px 0',
              }}>
                Person linking coming soon
              </div>
            </div>
          </div>
        ))}
        <AddItemBtn
          onClick={() => handleAddArrayItem('contacts', { title: '', contact_types: '', is_primary: false, personnel_profile_id: '' })}
          label="+ Contact"
        />
      </div>
    );
  }

  function renderRooms() {
    const rooms = formData.rooms || [];
    const ROOM_CARD = {
      backgroundColor: '#2a2a2a',
      border: '1px solid #3a3a3a',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '12px',
    };
    return (
      <div>
        <SectionLabel>ROOMS</SectionLabel>
        {rooms.length === 0 && (
          <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '12px' }}>
            No rooms added yet.
          </div>
        )}
        {rooms.map((room, i) => (
          <div key={i} style={ROOM_CARD}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5' }}>
                {room.title || <span style={{ color: '#565656', fontStyle: 'italic' }}>Untitled Room</span>}
              </div>
              {room.room_type && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  color: '#888888',
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 500,
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  marginLeft: '8px',
                  flexShrink: 0,
                }}>
                  {room.room_type}
                </span>
              )}
            </div>
            {room.location && (
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '13px', color: '#888888', marginBottom: '6px' }}>
                {room.location}
              </div>
            )}
            {room.attributes && room.attributes.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                {room.attributes.map((attr) => (
                  <span key={attr} style={{
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    color: '#888888',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 500,
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '10px',
                  }}>
                    {attr}
                  </span>
                ))}
              </div>
            )}
            {room.notes && (
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>
                {room.notes}
              </div>
            )}
          </div>
        ))}
        <GreenAddBtn onClick={() => console.log('Add Room clicked')} label="+ Room" />
      </div>
    );
  }

  function renderLoadingAndEquipment() {
    const docks = formData.loading?.docks || [];
    const equipment = formData.equipment || [];
    const EQUIP_CARD = {
      backgroundColor: '#2a2a2a',
      border: '1px solid #3a3a3a',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '12px',
    };
    const PAIR_LABEL = {
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 500,
      fontSize: '11px',
      color: '#565656',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '2px',
    };
    const PAIR_VALUE = {
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 400,
      fontSize: '14px',
      color: '#F5F5F5',
    };
    return (
      <div>
        {/* LOADING */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>LOADING</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.loading?.has_dock}
                onChange={(e) => handleCheckboxChange('loading.has_dock', e.target.checked)}
                style={CB_INPUT}
              />
              Has Dock
            </label>
          </div>
          <div style={{ ...GRID2, marginBottom: '16px' }}>
            {F('loading.load_in_area_desc', 'Load-In Area', '', true)}
            {F('loading.access_notes', 'Access Notes', '', true)}
          </div>
          <div style={{ marginBottom: '8px' }}>
            <FieldLabel>DOCK ADDRESS</FieldLabel>
          </div>
          <div style={GRID2}>
            {F('loading.dock_address1', 'Address 1', '')}
            {F('loading.dock_address2', 'Address 2', '')}
            {F('loading.dock_city', 'City', '')}
            {S('loading.dock_state', 'State', US_STATES, '— State —')}
            {F('loading.dock_zip', 'Zip', '')}
            {S('loading.dock_country', 'Country', COUNTRIES, '— Country —')}
          </div>
        </div>

        {/* DOCKS */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <SectionLabel>DOCKS</SectionLabel>
            <Indicator saved={savedFields['loading.docks']} error={errorFields['loading.docks']} />
          </div>
          {docks.length === 0 && (
            <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '12px' }}>
              No docks added yet.
            </div>
          )}
          {docks.map((dock, i) => (
            <div key={i} style={ITEM_CARD}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: '#888888' }}>
                  Dock {i + 1}
                </span>
                <RemoveItemBtn onClick={() => handleRemoveArrayItem('loading.docks', i)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 24px' }}>
                {AF('loading.docks', i, 'dock_number', 'Dock #', '')}
                {AS('loading.docks', i, 'dock_type', 'Type', DOCK_TYPE_ENUM, '— Type —')}
                {AF('loading.docks', i, 'distance_to_stage', 'Distance to Stage (ft)', '')}
                {AS('loading.docks', i, 'plate_type', 'Plate Type', PLATE_TYPE_ENUM, '— None —')}
                {AF('loading.docks', i, 'door_width', 'Door Width (ft)', '')}
                {AF('loading.docks', i, 'assignment', 'Assignment', '')}
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={CB_LABEL}>
                  <input
                    type="checkbox"
                    checked={!!dock.plate_available}
                    onChange={(e) => handleArrayItemCheckbox('loading.docks', i, 'plate_available', e.target.checked)}
                    style={CB_INPUT}
                  />
                  Plate Available
                </label>
              </div>
              <div style={{ marginTop: '12px' }}>
                {AF('loading.docks', i, 'notes', 'Notes', '', true)}
              </div>
            </div>
          ))}
          <AddItemBtn
            onClick={() => handleAddArrayItem('loading.docks', { dock_number: '', dock_type: '', distance_to_stage: '', plate_available: false, plate_type: '', door_width: '', assignment: '', notes: '' })}
            label="+ Dock"
          />
        </div>

        {/* EQUIPMENT */}
        <div style={SEC}>
          <SectionLabel>EQUIPMENT</SectionLabel>
          {equipment.length === 0 && (
            <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '12px' }}>
              No equipment added yet.
            </div>
          )}
          {equipment.map((item, i) => (
            <div key={i} style={EQUIP_CARD}>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5', marginBottom: '12px' }}>
                {item.equipment_type || <span style={{ color: '#565656', fontStyle: 'italic' }}>Unnamed Equipment</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 24px' }}>
                {[
                  { label: 'Load Capacity', value: item.load_capacity ? `${item.load_capacity} lbs` : '' },
                  { label: 'Height', value: item.height ? `${item.height} ft` : '' },
                  { label: 'Length', value: item.length ? `${item.length} ft` : '' },
                  { label: 'Width', value: item.width ? `${item.width} ft` : '' },
                  { label: 'Footprint', value: item.footprint },
                ].filter(({ value }) => value).map(({ label, value }) => (
                  <div key={label}>
                    <div style={PAIR_LABEL}>{label}</div>
                    <div style={PAIR_VALUE}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <GreenAddBtn onClick={() => console.log('Add Equipment clicked')} label="+ Equipment" />
        </div>
      </div>
    );
  }

  function renderPowerAndRigging() {
    const power = formData.power || [];
    const POWER_CARD = {
      backgroundColor: '#2a2a2a',
      border: '1px solid #3a3a3a',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '12px',
    };
    const PAIR_LABEL = {
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 500,
      fontSize: '11px',
      color: '#565656',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '2px',
    };
    const PAIR_VALUE = {
      fontFamily: "'Roboto', sans-serif",
      fontWeight: 400,
      fontSize: '14px',
      color: '#F5F5F5',
    };
    return (
      <div>
        {/* POWER */}
        <div style={SEC}>
          <SectionLabel>POWER</SectionLabel>
          {power.length === 0 && (
            <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '12px' }}>
              No power entries added yet.
            </div>
          )}
          {power.map((run, i) => (
            <div key={i} style={POWER_CARD}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 24px' }}>
                {[
                  { label: 'Type', value: run.type },
                  { label: 'Amperage', value: run.amperage },
                  { label: 'Phase', value: run.phase },
                  { label: 'Voltage', value: run.voltage },
                  { label: 'Transformer Needed', value: run.transformer_needed != null ? (run.transformer_needed ? 'Yes' : 'No') : '' },
                  { label: 'Location', value: run.location },
                  { label: 'Distance to USC', value: run.distance_to_usc !== '' && run.distance_to_usc != null ? `${run.distance_to_usc} ft` : '' },
                ].filter(({ value }) => value !== '' && value != null).map(({ label, value }) => (
                  <div key={label}>
                    <div style={PAIR_LABEL}>{label}</div>
                    <div style={PAIR_VALUE}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <GreenAddBtn onClick={() => console.log('Add Power Entry clicked')} label="+ Power Entry" />
        </div>

        {/* CAD */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>CAD</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.cad?.available}
                onChange={(e) => handleCheckboxChange('cad.available', e.target.checked)}
                style={CB_INPUT}
              />
              Available
            </label>
          </div>
          <div style={GRID2}>
            {F('cad.file_type', 'File Type', 'e.g. DWG, PDF')}
            {F('cad.url', 'CAD URL', 'https://', true)}
          </div>
        </div>

        {/* RIGGING */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>RIGGING</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.rigging?.available}
                onChange={(e) => handleCheckboxChange('rigging.available', e.target.checked)}
                style={CB_INPUT}
              />
              Available
            </label>
          </div>
          <div style={GRID3}>
            {F('rigging.trim_height', 'Trim Height (ft)', '')}
            {F('rigging.grid_weight', 'Grid Weight (lbs)', '')}
            {F('rigging.high_steel', 'High Steel (ft)', '')}
            {F('rigging.low_steel', 'Low Steel (ft)', '')}
            {F('rigging.scoreboard_trim', 'Scoreboard Trim (ft)', '')}
          </div>
          <div style={{ marginTop: '16px', ...GRID2 }}>
            {F('rigging.grid_access', 'Grid Access', '', true)}
            <div>
              <FieldLabel>Scoreboard</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.rigging?.scoreboard}
                  onChange={(e) => handleCheckboxChange('rigging.scoreboard', e.target.checked)}
                  style={CB_INPUT}
                />
                Present
              </label>
            </div>
            <div>
              <FieldLabel>Scoreboard Moves</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.rigging?.scoreboard_moves}
                  onChange={(e) => handleCheckboxChange('rigging.scoreboard_moves', e.target.checked)}
                  style={CB_INPUT}
                />
                Yes
              </label>
            </div>
            <div>
              <FieldLabel>Diagram Available</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.rigging?.diagram_available}
                  onChange={(e) => handleCheckboxChange('rigging.diagram_available', e.target.checked)}
                  style={CB_INPUT}
                />
                Yes
              </label>
            </div>
            {F('rigging.diagram_file', 'Diagram File', 'File path or URL', true)}
            {F('rigging.notes', 'Notes', '', true)}
          </div>
        </div>
      </div>
    );
  }

  function renderProduction() {
    return (
      <div>
        {/* STAGE */}
        <div style={SEC}>
          <SectionLabel>STAGE</SectionLabel>
          <div style={GRID3}>
            {S('stage.type', 'Stage Type', STAGE_TYPE_ENUM, '— Type —')}
            {F('stage.height', 'Height (ft)', '')}
            {F('stage.provider', 'Provider', '')}
          </div>
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
            <div>
              <FieldLabel>House Drape</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.stage?.house_drape}
                  onChange={(e) => handleCheckboxChange('stage.house_drape', e.target.checked)}
                  style={CB_INPUT}
                />
                Available
              </label>
            </div>
            {F('stage.house_drape_notes', 'House Drape Notes', '')}
            <div>
              <FieldLabel>Barricade</FieldLabel>
              <label style={CB_LABEL}>
                <input
                  type="checkbox"
                  checked={!!formData.stage?.barricade}
                  onChange={(e) => handleCheckboxChange('stage.barricade', e.target.checked)}
                  style={CB_INPUT}
                />
                Available
              </label>
            </div>
            {F('stage.barricade_notes', 'Barricade Notes', '')}
            {F('stage.mobile_stage_notes', 'Mobile Stage Notes', '', true)}
            {F('stage.attribute_notes', 'Attribute Notes', '', true)}
          </div>
        </div>

        {/* CAMERA RISERS */}
        <div style={SEC}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <SectionLabel>CAMERA RISERS</SectionLabel>
            <label style={{ ...CB_LABEL, marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={!!formData.camera_risers?.available}
                onChange={(e) => handleCheckboxChange('camera_risers.available', e.target.checked)}
                style={CB_INPUT}
              />
              Available
            </label>
          </div>
          {F('camera_risers.notes', 'Notes', '', true)}
        </div>

        {/* AUDIO */}
        <div style={SEC}>
          <SectionLabel>AUDIO</SectionLabel>
          <div style={GRID2}>
            {F('production.audio.description', 'Description', '')}
            {S('production.audio.configuration', 'Configuration', AUDIO_CONFIGURATION_ENUM, '— Config —')}
            {F('production.audio.patch', 'Patch', '', true)}
          </div>
        </div>

        {/* LIGHTING */}
        <div style={SEC}>
          <SectionLabel>LIGHTING</SectionLabel>
          <div style={GRID2}>
            {F('production.lighting.description', 'Description', '')}
            {S('production.lighting.control_language', 'Control Language', LIGHTING_CONTROL_ENUM, '— Protocol —')}
            {F('production.lighting.console_file', 'Console File', '')}
            {F('production.lighting.plot_file', 'Plot File', '')}
            {F('production.lighting.patch_file', 'Patch File', '', true)}
          </div>
        </div>

        {/* VIDEO */}
        <div style={SEC}>
          <SectionLabel>VIDEO</SectionLabel>
          <div style={GRID2}>
            {F('production.video.description', 'Description', '')}
            {F('production.video.format', 'Format', '')}
            {F('production.video.input', 'Input', '')}
            {F('production.video.patch', 'Patch', '')}
            {F('production.video.raster_file', 'Raster File', '', true)}
          </div>
        </div>
      </div>
    );
  }

  function renderTabContent() {
    switch (activeTab) {
      case 'Details': return renderDetails();
      case 'Contacts': return renderContacts();
      case 'Rooms': return renderRooms();
      case 'Loading & Equipment': return renderLoadingAndEquipment();
      case 'Power & Rigging': return renderPowerAndRigging();
      case 'Production': return renderProduction();
      default: return null;
    }
  }

  const locationLine = [venue.venue_type, [venue.city, venue.state].filter(Boolean).join(', ')]
    .filter(Boolean).join(' · ');

  return (
    <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#121212' }}>
      {/* Header */}
      <div style={{
        padding: '32px 40px 24px 40px',
        borderBottom: '1px solid #2a2a2a',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: '#1e1e1e',
          border: '2px solid #39FF14',
          boxShadow: '0 0 16px rgba(57,255,20,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <LocationCityOutlined style={{ fontSize: 28, color: '#39FF14' }} />
        </div>
        <div>
          <div style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 900,
            fontSize: '28px',
            color: '#FFFFFF',
            letterSpacing: '0.5px',
          }}>
            {venue.name}
          </div>
          {locationLine && (
            <div style={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 400,
              fontSize: '14px',
              color: '#888888',
              marginTop: '2px',
            }}>
              {locationLine}
            </div>
          )}
          <div style={{
            display: 'inline-block',
            backgroundColor: 'rgba(57, 255, 20, 0.08)',
            border: '1px solid rgba(57, 255, 20, 0.2)',
            borderRadius: '10px',
            padding: '2px 10px',
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '11px',
            color: '#39FF14',
            letterSpacing: '1px',
            marginTop: '6px',
          }}>
            GLOBAL
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #2a2a2a',
        padding: '0 40px',
        overflowX: 'auto',
      }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${activeTab === tab ? '#39FF14' : 'transparent'}`,
              color: activeTab === tab ? '#39FF14' : '#888888',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '13px',
              padding: '12px 16px 10px 16px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
              letterSpacing: '0.3px',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '28px 40px 48px 40px' }}>
        {renderTabContent()}
      </div>
    </div>
  );
}
